// Mercapoli - Native App Wrapper
// Handles native app initialization and network state
// Use this to wrap your main App component

import React, { useEffect, useState } from 'react';
import capacitorService, { isNative, platform } from '../services/capacitor';
import OfflineScreen from './OfflineScreen';
import LoadingScreen from './LoadingScreen';

const NativeAppWrapper = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(!isNative);
  const [isOnline, setIsOnline] = useState(true);
  const [showOffline, setShowOffline] = useState(false);

  useEffect(() => {
    const initApp = async () => {
      if (!isNative) {
        setIsInitialized(true);
        return;
      }

      try {
        // Initialize native features
        await capacitorService.initNativeApp();

        // Check initial network status
        const networkStatus = await capacitorService.getNetworkStatus();
        setIsOnline(networkStatus.connected);
        setShowOffline(!networkStatus.connected);

        // Listen for network changes
        capacitorService.onNetworkChange((status) => {
          setIsOnline(status.connected);
          if (!status.connected) {
            setShowOffline(true);
          } else {
            setShowOffline(false);
          }
        });

        // Setup back button handler for Android
        if (platform === 'android') {
          capacitorService.setupBackButton((event) => {
            if (!event.canGoBack) {
              // Could show exit confirmation here
              // For now, just go back in browser history
              window.history.back();
            }
          });
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing native app:', error);
        setIsInitialized(true); // Continue anyway
      }
    };

    initApp();
  }, []);

  // Handle retry when offline
  const handleRetry = async () => {
    const networkStatus = await capacitorService.getNetworkStatus();
    if (networkStatus.connected) {
      setShowOffline(false);
      setIsOnline(true);
    }
  };

  // Show loading screen while initializing native features
  if (!isInitialized) {
    return <LoadingScreen message="Iniciando Mercapoli..." />;
  }

  // Show offline screen when no connection
  if (showOffline && !isOnline) {
    return <OfflineScreen onRetry={handleRetry} />;
  }

  return children;
};

// Web fallback - also handles browser online/offline events
const WebNetworkWrapper = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) {
    return <OfflineScreen onRetry={() => window.location.reload()} />;
  }

  return children;
};

// Export the appropriate wrapper based on platform
const AppWrapper = isNative ? NativeAppWrapper : WebNetworkWrapper;

export default AppWrapper;
